
/* eslint-disable @typescript-eslint/camelcase */
import { Options, prop, Vue } from "vue-class-component";
import Tooltip from "@/app/ui/components/tooltip/index.vue";
import ScheduleCard from "./schedule-card.vue";
import {
  ellipsisString,
  firstCapitalize,
  formatDateWithoutTime,
  formatByCountry,
  parseByCountry
} from "@/app/infrastructures/misc/Utils";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { CargoController } from "@/app/ui/controllers/CargoController";
import { LocationController } from "@/app/ui/controllers/LocationController";
import { NGenController } from "@/app/ui/controllers/NGenController";
import { GetScheduleListApiRequest, NGenSearchFlightV3Request } from "@/data/payload/api/NGenApiRequest";
import { FlightSchedule, FlightDetail } from "@/domain/entities/NGen";
import HeaderScheduleCard from "@/app/ui/views/cargo/booking-cargo/components/plane-schedule/header-schedule-card.vue";
import HorizontalScrollContainer from "@/app/ui/components/horizontal-scroll-container/index.vue";
import groupBy from "lodash/groupBy";
import { dataLayer } from "@/app/infrastructures/misc/UtilsGtm";
import { gtmCargoData } from "@/app/ui/views/cargo/modules/gtm";
import { ReadyToCargoController } from "@/app/ui/controllers/ReadyToCargoController";
import { defineAsyncComponent } from "vue";
import { FlagsPermissionCargo } from "@/feature-flags/flags-permission-cargo";
import LayoutSchedule from "./layout-schedule-v2.vue";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { ModalMessageEntities } from "@/domain/entities/MainApp";
import { flags } from "@/feature-flags";

class Props {
  isShowDate = prop<boolean>({
    type: Boolean,
    default: false
  });
  width = prop<string>({
    type: String,
    default: "w-full"
  });
}
const CargoProcessCargo = defineAsyncComponent({
  loader: () =>
    import(
      "@/app/ui/views/cargo/booking-cargo/third-step/cargo-process-cargo.vue"
    )
});

@Options({
  emits: ["change-airport"],
  components: {
    HeaderScheduleCard,
    Tooltip,
    ScheduleCard,
    HorizontalScrollContainer,
    CargoProcessCargo,
    LayoutSchedule
  },
  watch: {
    flightsData() {
      if (this.flightsData.length === 0) {
        this.flagFeatureFlight ? this.getSearchFlight() : this.onGetFlightSchedule(this.flightsData.length);
      }
    }
  }
})
export default class PlaneSchedule extends Vue.with(Props) {
  get countryCode() {
    return AccountController.accountData?.countryCode ?? "ID";
  }

  mounted() {
    this.flagFeatureFlight ? this.getSearchFlight() : this.onGetFlightSchedule(this.form.flightsData.length);
    // this.form.actualTotalCargoGrossWeight = this.cargoInputForm.totalActualCargoGrossWeight.toString();
  }
  unmounted() {
    this.scheduleController?.abort();
  }

  get cargoInputForm() {
    return CargoController.cargoInputForm;
  }

  get readyToCargoDetail() {
    return ReadyToCargoController.readyToCargoDetail;
  }

  get isRtc() {
    return this.form.isRtc;
  }

  firstCapitalize(str: string) {
    if (str) {
      return firstCapitalize(str.toLowerCase());
    }
    return "";
  }

  get form() {
    return CargoController.form;
  }
  get flightsData() {
    return this.form.flightsData;
  }

  // origin city
  get detailDistrict() {
    return LocationController.districtDetail;
  }

  // list flight schedule
  flightSchedules: FlightSchedule = new FlightSchedule();
  get filteredFlightSchedules() {
    return groupBy(
      this.flightSchedules.flightDetails.filter((data: FlightDetail) => {
        if (!this.form.flightsData.length) {
          return this.$moment(data.DepartureDateTime).isSameOrBefore(
            this.$moment(this.form.estDepartureDate).add(2, "day"),
            "day"
          );
        }
        return this.$moment(data.DepartureDateTime).isBetween(
          this.form.flightsData[this.form.flightsData.length - 1]
            .ArrivalDateTime,
          this.$moment(
            this.form.flightsData[this.form.flightsData.length - 1]
              .ArrivalDateTime
          ).add(2, "day")
        );
      }),
      (flight: FlightDetail) => {
        return this.$moment(flight.DepartureDateTime).format("YYYY-MM-DD");
      }
    );
  }

  scheduleController = new AbortController();

  async onGetFlightSchedule(amountFlightData: number) {
    this.scheduleController?.abort();
    this.scheduleController = new AbortController();

    const origin = this.form.airports[this.form.flightsData.length]
    const destination = this.form.airports[this.form.flightsData.length + 1]

    if (FlagsPermissionCargo.permission_ngen_search_flight_v3.isEnabled()) {
      this.flightSchedules = await NGenController.getNGenFlightSearchV3({
        signal: this.scheduleController.signal,
        cityCode: "",
        payload: new NGenSearchFlightV3Request({
          Origin: origin?.airportCode,
          Destination: destination.airportCode,
          FlightDate: this.$moment(this.form.estDepartureDate).format(
            "YYYY-MM-DD"
          ),
          GrossWeight: String(
            this.parseNumber(this.form.actualTotalCargoGrossWeight)
          ),
          VolumeWeight: String(
            this.parseNumber(this.form.actualTotalCargoVolumeWeight)
          ),
          ETD: "00",
          IsRootStation: amountFlightData > 0 ? true : false,
          FlightNo: "",
          ParticipantID: origin.participantId,
          Office: origin.office,
          ProductName: this.form.cargoProduct
        })
      })
    } else {
      this.flightSchedules = await NGenController.onGetScheduleList({
        signal: this.scheduleController.signal,
        cityCode: "",
        payload: new GetScheduleListApiRequest({
          Origin: origin?.airportCode,
          Destination: destination?.airportCode,
          FlightDate: this.$moment(this.form.estDepartureDate).format(
            "YYYY-MM-DD"
          ),
          GrossWeight: String(
            this.parseNumber(this.form.actualTotalCargoGrossWeight)
          ),
          VolumeWeight: String(
            this.parseNumber(this.form.actualTotalCargoVolumeWeight)
          ),
          ETD: "00",
          ProductSno: "37",
          IsRootStation: amountFlightData > 0 ? 1 : 0,
          FlightNo: ""
        })
      });
    }
    // GTM
    if (this.form.isChangeAirport) {
      const gtmProperty = gtmCargoData({
        type: this.form.cargoType,
        isAuto: true,
        gtmData: {
          totalFlights: this.flightSchedules.flightDetails.length
        }
      }).changeAirport;

      dataLayer(gtmProperty.eventName, gtmProperty.property, []);
    } else {
      const flightSchedules = gtmCargoData({
        type: this.form.cargoType,
        isAuto: true,
        gtmData: {
          totalFlights: this.flightSchedules.flightDetails.length
        }
      })?.planeSchedule;

      dataLayer(flightSchedules?.eventName, flightSchedules?.property, []);
    }
  }

  // departure date
  get estDepartureDate() {
    return formatDateWithoutTime(this.form.estDepartureDate);
  }

  get isLoading() {
    return NGenController.isLoading;
  }
  get errorData() {
    return NGenController.errorData;
  }

  ellipsisString(value: string) {
    return ellipsisString(value, this.isShowDate ? 20 : 40);
  }

  get labelTransit() {
    if (this.form.airports.length === 2) {
      return "";
    }
    return `${this.form.airports.length - 2} Transit`;
  }
  get labelPlaneSchedule() {
    if (this.form.airports.length === 2) {
      return "Menampilkan semua kargo pesawat ke kota tujuan Anda.";
    }
    if (this.form.flightsData.length === this.form.airports.length - 2) {
      return `Menampilkan semua kargo pesawat ke bandara tujuan Anda.`;
    }
    return `Menampilkan semua kargo pesawat ke bandara transit ${this.form
      .flightsData.length + 1}`;
  }

  get scheduleFlight() {
    const schedules = [];
    for (let i = 0; i < this.form.airports.length - 1; i++) {
      schedules.push({
        origin: `${this.form.airports[i].airportName}`,
        destination: `${this.form.airports[i + 1].airportName}`
      });
    }
    return schedules;
  }

  moment(value: any) {
    return this.$moment(value);
  }

  get showSelectAirport() {
    return CargoController.showSelectAirport;
  }

  handleConfirmation() {
    MainAppController.showMessageModal(
      new ModalMessageEntities({
        image: "badge-confirmation-general",
        title: "Konfirmasi pembuatan?",
        message: "Pastikan kembali seluruh detail STT sudah benar dan sesuai.",
        onSubmit: this.onBookingCargo,
        onClose: () => {
          MainAppController.closeMessageModal();
          CargoController.resetFlightData();
          this.onGetFlightSchedule(this.form.flightsData.length)
        },
        textSuccess: "Proses",
        textCancel: "Batal",
        customWidth: "417px",
        customButton: "w-full",
        customParentButton: "w-full",
        customWrapperButton: "w-full px-6",
      })
    );
  }

  async onBookingCargo() {
    MainAppController.closeMessageModal();
    await (this.$refs.bookingCargo as any).onCheckEligible();
    CargoController.setIsChangeCargoType(true);
  }

  onLeavePage() {
    ReadyToCargoController.setFlowBooking("");
    CargoController.resetCargoData();
  }

  // v2
  get flagFeatureFlight() {
    return FlagsPermissionCargo.feature_flight_v2_enable.isEnabled()
  }

  get searchFlight() {
    return CargoController.searchFlight;
  }

  get isLoadingSearchFlight() {
    return CargoController.isLoadingSearchFlight;
  }

  get errorCauseSearchFlight() {
    return CargoController.errorCauseSearchFlight;
  }

  private parseNumber(value: string) {
    if (typeof value === "number") return value;
    return value ? parseByCountry(value, { country: this.countryCode }) : 0;
  }

  async getSearchFlight() {
    await CargoController.getSearchFlight({
      departureDate: this.$moment(this.form.estDepartureDate).format("YYYY-MM-DD"),
      originCityCode: this.form.airportOrigin.cityCode,
      originAirportCode: this.form.airportOrigin.airportCode,
      destinationAirportCode: this.form.airportDestination.airportCode,
      actualTotalGrossWeight: this.parseNumber(
        this.form.actualTotalCargoGrossWeight
      ),
      actualTotalVolumeWeight: this.parseNumber(
        this.form.actualTotalCargoVolumeWeight
      ),
      thresholdTime: +this.form.thresholdTime,
      commodityGroupCode: this.form.cargoCommodityCode
    });
  }

  // new dimension per koli
  get isPlaneAuto() {
    return (
      this.form.cargoType === "plane" && this.form.cargoTypeStatus === "auto"
    );
  }
  get isValidDimension(): boolean {
    return CargoController.dimensionPerKoliData.dimensionPieces.every(
      e => e.sttOrBag
    );
  }
  get isDimensionPlaneAutoV2(): boolean {
    const isRtcOrRebooking = this.isRtc && this.form.cargoType === "plane";
    return Boolean(
      !flags.feature_hide_dimension_koli.isEnabled() &&
        this.isValidDimension &&
        (this.isPlaneAuto || isRtcOrRebooking)
    );
  }
  get actualGrossWeight(): string {
    return this.isDimensionPlaneAutoV2
      ? formatByCountry(CargoController.dimensionPerKoliData.actualGrossWeight, { type: "weight", country: this.countryCode })
      : this.form.actualTotalCargoGrossWeight;
  }
  get actualVolumeWeight(): string {
    // CargoController.calculationVolumeWeight(
    //   Number(CargoController.cargoInputForm.totalActualCargoColi)
    // );

    return this.isDimensionPlaneAutoV2
      ? formatByCountry(CargoController.dimensionPerKoliData.actualVolumeWeight, { type: "weight", country: this.countryCode })
      : this.form.actualTotalCargoVolumeWeight;
  }
}
